import { createContext, useContext, useState, useEffect } from 'react'
import { doc, onSnapshot } from 'firebase/firestore'
import { db } from '../lib/firebase'

const SiteSettingsContext = createContext(null)

// Default settings - matches original Pel-o Ingolé theme
const DEFAULT_SETTINGS = {
  siteName: 'Pel -o Ingolé',
  siteTagline: 'A D&D Campaign',
  // Theme colors
  primaryColor: '#6E2EBF',    // violet-core
  primaryHover: '#9B5FFF',    // ember-violet
  primaryMuted: '#4B1F7A',    // deep-amethyst
  accentColor: '#7A1E2E',     // blood-wine
  bgMain: '#141018',          // obsidian
  bgSurface: '#1E1A22',       // ashen-char
  bgElevated: '#0B0B0F',      // black-void
  textPrimary: '#C9C6CF',     // silver-ash
  textSecondary: '#ACA9B5',   // dull-steel
  textHeading: '#E6E4EA',     // moon-sheen
}

export function useSiteSettings() {
  const context = useContext(SiteSettingsContext)
  if (!context) {
    throw new Error('useSiteSettings must be used within a SiteSettingsProvider')
  }
  return context
}

export function SiteSettingsProvider({ children }) {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS)
  const [loading, setLoading] = useState(true)

  // Load settings from Firestore
  useEffect(() => {
    const unsubscribe = onSnapshot(
      doc(db, 'config', 'siteSettings'),
      (snapshot) => {
        if (snapshot.exists()) {
          setSettings({ ...DEFAULT_SETTINGS, ...snapshot.data() })
        } else {
          setSettings(DEFAULT_SETTINGS)
        }
        setLoading(false)
      },
      (error) => {
        console.error('Error loading site settings:', error)
        setSettings(DEFAULT_SETTINGS)
        setLoading(false)
      }
    )

    return unsubscribe
  }, [])

  // Apply theme colors to CSS custom properties
  useEffect(() => {
    const root = document.documentElement

    // Primary/Accent colors
    root.style.setProperty('--color-accent-primary', settings.primaryColor)
    root.style.setProperty('--color-accent-hover', settings.primaryHover)
    root.style.setProperty('--color-accent-muted', settings.primaryMuted)
    root.style.setProperty('--color-danger', settings.accentColor)

    // Background colors
    root.style.setProperty('--color-bg-main', settings.bgMain)
    root.style.setProperty('--color-bg-surface', settings.bgSurface)
    root.style.setProperty('--color-bg-elevated', settings.bgElevated)

    // Text colors
    root.style.setProperty('--color-text-primary', settings.textPrimary)
    root.style.setProperty('--color-text-secondary', settings.textSecondary)
    root.style.setProperty('--color-text-heading', settings.textHeading)

    // Also set the base variables for components that use them directly
    root.style.setProperty('--violet-core', settings.primaryColor)
    root.style.setProperty('--ember-violet', settings.primaryHover)
    root.style.setProperty('--deep-amethyst', settings.primaryMuted)
    root.style.setProperty('--blood-wine', settings.accentColor)
    root.style.setProperty('--obsidian', settings.bgMain)
    root.style.setProperty('--ashen-char', settings.bgSurface)
    root.style.setProperty('--black-void', settings.bgElevated)
    root.style.setProperty('--silver-ash', settings.textPrimary)
    root.style.setProperty('--dull-steel', settings.textSecondary)
    root.style.setProperty('--moon-sheen', settings.textHeading)
  }, [settings])

  // Update document title
  useEffect(() => {
    document.title = settings.siteName
  }, [settings.siteName])

  const value = {
    settings,
    loading,
    siteName: settings.siteName,
    siteTagline: settings.siteTagline,
  }

  return (
    <SiteSettingsContext.Provider value={value}>
      {children}
    </SiteSettingsContext.Provider>
  )
}
